// Defaults applied to a fresh browser profile on this public deployment:
// the remote streaming server, the proxied public addon, and the official
// metadata catalog. Cinemeta supplies trailers from legitimate YouTube IDs.
// Each default is applied at most once, so later user choices are preserved.

const React = require('react');
const { useCore } = require('stremio/core');
const { withCoreSuspender, useProfile } = require('stremio/common');

const STREAMING_SERVER_URL = 'https://server.banane.fun/';
const ADDON_TRANSPORT_URL = 'https://stream.banane.fun/addon/manifest.json';
const CINEMETA_TRANSPORT_URL = 'https://v3-cinemeta.strem.io/manifest.json';
const SERVER_APPLIED_KEY = 'croissantdufutur.public-defaults.server.v1';
// Adresse par defaut de l'application de bureau Stremio. Se connecter a un
// compte remplace les reglages locaux par ceux du compte, qui portent cette
// valeur : le site perd alors son serveur de streaming a chaque connexion.
const STREMIO_LOCAL_DEFAULT = 'http://127.0.0.1:11470/';
const ADDON_APPLIED_KEY = 'croissantdufutur.public-defaults.addon.v1';
const CINEMETA_APPLIED_KEY = 'croissantdufutur.public-defaults.cinemeta.v1';

const readFlag = (key) => {
    try {
        return window.localStorage.getItem(key);
    } catch (error) {
        return null;
    }
};

const writeFlag = (key) => {
    try {
        window.localStorage.setItem(key, 'applied');
    } catch (error) {
        // Storage unavailable: the default is simply re-applied next time.
    }
};

const PublicDefaultsHandler = () => {
    const core = useCore();
    const profile = useProfile();

    React.useEffect(() => {
        const current = profile.settings.streamingServerUrl;

        // Une adresse locale ne peut pas servir depuis ce site : on la corrige
        // toujours, meme si le defaut a deja ete applique une fois. C'est le
        // seul cas ou l'on repasse derriere le reglage courant.
        if (current !== STREMIO_LOCAL_DEFAULT && readFlag(SERVER_APPLIED_KEY) === 'applied')
            return;

        // An explicit ?streamingServerUrl= wins over this deployment's default:
        // SearchParamsHandler applies it and we must not clobber it on first load.
        const { origin, hash, search } = window.location;
        const requestedServerUrl = new URL(`${origin}${hash.replace('#', '')}${search}`)
            .searchParams.get('streamingServerUrl');

        if (!requestedServerUrl && current !== STREAMING_SERVER_URL) {
            core.transport.dispatch({
                action: 'Ctx',
                args: {
                    action: 'UpdateSettings',
                    args: {
                        ...profile.settings,
                        streamingServerUrl: STREAMING_SERVER_URL,
                    },
                },
            });
            core.transport.dispatch({
                action: 'Ctx',
                args: {
                    action: 'AddServerUrl',
                    args: STREAMING_SERVER_URL,
                },
            });
        }

        writeFlag(SERVER_APPLIED_KEY);
    }, [profile]);

    React.useEffect(() => {
        if (readFlag(ADDON_APPLIED_KEY) === 'applied')
            return;

        fetch(ADDON_TRANSPORT_URL)
            .then((response) => {
                if (!response.ok)
                    throw new Error(`public addon manifest returned ${response.status}`);
                return response.json();
            })
            .then((manifest) => {
                core.transport.dispatch({
                    action: 'Ctx',
                    args: {
                        action: 'InstallAddon',
                        args: {
                            transportUrl: ADDON_TRANSPORT_URL,
                            manifest,
                        },
                    },
                });
                writeFlag(ADDON_APPLIED_KEY);
            })
            .catch(() => {
                // Deliberately silent: nothing about the addon is written to the console.
            });
    }, []);

    // Cinemeta is Stremio's official metadata catalog. It provides movie and
    // series pages, including trailer streams, without adding any media source.
    React.useEffect(() => {
        if (readFlag(CINEMETA_APPLIED_KEY) === 'applied')
            return;

        fetch(CINEMETA_TRANSPORT_URL)
            .then((response) => {
                if (!response.ok)
                    throw new Error(`Cinemeta manifest returned ${response.status}`);
                return response.json();
            })
            .then((manifest) => {
                core.transport.dispatch({
                    action: 'Ctx',
                    args: {
                        action: 'InstallAddon',
                        args: {
                            transportUrl: CINEMETA_TRANSPORT_URL,
                            manifest,
                        },
                    },
                });
                writeFlag(CINEMETA_APPLIED_KEY);
            })
            .catch(() => {
                // The base application remains usable if the catalog is unavailable.
            });
    }, []);

    return null;
};

module.exports = withCoreSuspender(PublicDefaultsHandler);
